import ajax from '../../common/services/httpService';
const BASE_URL = 'auth'

/**
 * @param {string} code
 * @returns {Error & { code: string, validationCode?: string, status?: number }}
 */
function loginError(code, status) {
    const err = new Error(code);
    err.code = code;
    if (status != null) err.status = status;
    return err;
}

/**
 * @param {Object} userCred
 * @param {string} [userCred.email]
 * @param {string} [userCred.username]
 * @param {string} userCred.password
 */
async function login(userCred) {
    try {
        // silent: Login.vue owns the UI message (no toast popup)
        return await ajax.post(`${BASE_URL}/login`, userCred, { silent: true });
    } catch (err) {
        const code = err && (err.validationCode || err.code);
        if (code === 'INVALID_CREDENTIALS' || code === 'CREDENTIALS_REQUIRED' || code === 'LOGIN_FAILED' || code === 'TOO_MANY_ATTEMPTS') {
            throw loginError(code, err.status);
        }
        if (err && err.status === 400) {
            throw loginError('INVALID_CREDENTIALS', 400);
        }
        if (err && typeof err.message === 'string' && /network|unable to reach|timed out/i.test(err.message)) {
            throw loginError('LOGIN_NETWORK_ERROR');
        }
        throw loginError('LOGIN_FAILED');
    }
}

async function getLoggedInUser() {
    try {
        const user = await ajax.get(`${BASE_URL}/loggedInUser`);
        return user
    } catch (err) {
        throw new Error('Could not get user');
    }
}

const API_ORIGIN = process.env.NODE_ENV === 'production'
    ? '/api/'
    : 'http://localhost:3000/api/'

/** @returns {Promise<boolean>} */
async function isEntraSsoEnabled() {
    try {
        const result = await ajax.get(`${BASE_URL}/sso/entra/status`, null, null, { silent: true })
        return !!(result && result.enabled)
    } catch (err) {
        return false
    }
}

/** Full-page redirect to Microsoft Entra (not XHR). */
function startEntraSso() {
    window.location.href = `${API_ORIGIN}auth/sso/entra/start`
}

async function logout() {
    try {
        await ajax.post(`${BASE_URL}/logout`)
        sessionStorage.clear()
        return true
    } catch (err) {
        throw new Error('Could not log out');
    }
}

/**@param {string} email */
async function isEmailValid(email) {
    try {
        const isValid = await ajax.get(`${BASE_URL}/is-email-valid`, null, { email });
        return isValid;
    } catch (err) {
        throw new Error('server had problem testing email');
    }
}

async function isUsernameValid(username) {
    try {
        const isValid = await ajax.get(`${BASE_URL}/is-username-valid`, null, { username });
        return isValid;
    } catch (err) {
        throw new Error('server had problem testing username');
    }
}

async function isPhoneValid(phone) {
    try {
        const isValid = await ajax.get(`${BASE_URL}/is-phone-valid`, null, { phone });
        return isValid;
    } catch (err) {
        throw new Error('server had problem testing phone');
    }
}

/**
 * Change the password of the logged-in user.
 * Backend errors arrive as err.validationCode (e.g. 'CURRENT_PASSWORD_INCORRECT').
 * @param {string} currentPassword
 * @param {string} newPassword
 */
async function changePassword(currentPassword, newPassword) {
    return ajax.put(`${BASE_URL}/change-password`, { currentPassword, newPassword });
}

/**
 * Request a password-reset email. Backend always returns a generic success message.
 * @param {string} email
 */
async function forgotPassword(email) {
    return ajax.post(`${BASE_URL}/forgot-password`, { email });
}

/**
 * Complete password reset with the one-time token from email.
 * @param {string} token
 * @param {string} newPassword
 */
async function resetPassword(token, newPassword) {
    return ajax.post(`${BASE_URL}/reset-password`, { token, newPassword }, { silent: true });
}

export default {
    login,
    isEmailValid,
    getLoggedInUser,
    logout,
    isUsernameValid,
    isPhoneValid,
    changePassword,
    forgotPassword,
    resetPassword,
    isEntraSsoEnabled,
    startEntraSso,
}
